import React from 'react';
import { sha256 } from 'js-sha256';
import { ClompApi } from '../api/ClompApi';
import { GOOGLE_CLIENT_ID } from '../config';
import { GoogleSignInButton } from './GoogleSignInButton';
import { FRIENDS_VISIBILITY_NAMES } from './Friends';

const api = new ClompApi();

const PASSWORD_MIN_LENGTH = 8;
// How long typing has to pause before a new username is checked
const USERNAME_CHECK_DELAY = 350;
// What every input in the form has, so browsers and password managers don't offer to fill it in
const NO_FILL = { autoComplete: 'off', 'data-lpignore': 'true', 'data-1p-ignore': 'true', 'data-bwignore': 'true', 'data-form-type': 'other' };

// The marks shown at the right of the username input: whether the name typed can be had
function AvailableIcon({ available }) {
    return (
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            {available ?
                <path fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" d="M5 12.5l4.5 4.5L19 7.5" />
                :
                <path fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
            }
        </svg>
    );
}

// The settings wheel on your own profile
export function SettingsIcon() {
    return (
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path fill="currentColor" d="M19.14 12.94c.04-.3.06-.61.06-.94s-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96a7 7 0 0 0-1.62-.94l-.36-2.54a.48.48 0 0 0-.48-.41h-3.84a.48.48 0 0 0-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.48.48 0 0 0-.59.22L2.74 8.87a.48.48 0 0 0 .12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32a.48.48 0 0 0-.12-.61l-2.01-1.58zM12 15.6a3.6 3.6 0 1 1 0-7.2 3.6 3.6 0 0 1 0 7.2z" />
        </svg>
    );
}

// The account settings, shown over the profile, in four parts each with its own button: the profile details
// (username, email, bio, privacy), changing the password, linking a Google account, and deleting the account.
//   onClose() - the form was closed without saving; onSaved(settings) - saved, with the account as it now is;
//   onDeleted() - the account has been deleted
export class ProfileSettings extends React.Component {

    state = {
        loading: true,
        // the account as it was when the form opened: { username, email, bio, hasPassword, googleLinked, ...limits }
        saved: null,
        username: '',
        email: '',
        bio: '',
        // other players can't see whether this account is online or when it last was
        hidePresence: false,
        // who can see the friends list on the profile: 'public', 'friends' or 'private'
        friendsVisibility: 'public',
        // the current password, asked for in the details form when the email is being changed
        currentPassword: '',
        // Whether the username typed can be had, checked as it is typed: null while it is the saved one (or
        // nothing has been checked yet), otherwise { name, status, message } where status is 'checking', 'available'
        // or 'unavailable' and message says why it can't be had
        usernameCheck: null,
        saving: false,
        // { field, message }: field names the input the message belongs to, or is null for the whole form
        error: null,
        // Changing the password: the current one, the new one typed twice, how it is going, and whether it has
        // just been changed
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
        changingPassword: false,
        passwordError: null,
        passwordChanged: false,
        // Linking or unlinking a Google account: the current password that confirms it, whether the unlink
        // confirmation is showing, how it is going, and what has just been done ('linked' or 'unlinked')
        googlePassword: '',
        confirmingUnlink: false,
        linking: false,
        googleError: null,
        googleDone: null,
        // Deleting the account: whether the confirmation is showing, what has been typed into it (the password,
        // or the username for an account that only signs in with Google), and how it is going
        confirmingDelete: false,
        deleteConfirm: '',
        deleting: false,
        deleteError: null
    };

    componentDidMount() {
        window.addEventListener('keydown', this.onKeyDown);
        api.profile.getSettings().then((saved) => {
            this.setState({ loading: false, saved, username: saved.username, email: saved.email, bio: saved.bio, hidePresence: Boolean(saved.hidePresence), friendsVisibility: saved.friendsVisibility || 'public' });
        }).catch((err) => {
            console.log('Error loading settings: ', err);
            this.setState({ loading: false, error: { field: null, message: "Couldn't load your settings. Please try again." } });
        });
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.onKeyDown);
        clearTimeout(this.usernameTimer);
    }

    onUsernameChange = (e) => {
        let username = e.target.value;
        this.setState({ username });
        this.checkUsername(username.trim());
    }

    // Ask whether a username can be had, once typing has paused, unless it is the one already saved
    checkUsername(name) {
        clearTimeout(this.usernameTimer);
        if (name === '' || name === this.state.saved.username) {
            this.setState({ usernameCheck: null });
            return;
        }
        this.setState({ usernameCheck: { name, status: 'checking', message: null } });
        this.usernameTimer = setTimeout(() => {
            api.profile.checkUsername(name).then((result) => {
                // only if it is still what's typed
                if (this.state.usernameCheck && this.state.usernameCheck.name === name) {
                    this.setState({ usernameCheck: { name, status: result.available ? 'available' : 'unavailable', message: result.message } });
                }
            }).catch((err) => {
                console.log('Error checking username: ', err);
                if (this.state.usernameCheck && this.state.usernameCheck.name === name) {
                    this.setState({ usernameCheck: null });
                }
            });
        }, USERNAME_CHECK_DELAY);
    }

    onKeyDown = (e) => {
        if (e.key === 'Escape' && !this.busy()) {
            this.props.onClose();
        }
    }

    // Whether one of the parts is being saved, so the form shouldn't be closed under it
    busy() {
        return this.state.saving || this.state.changingPassword || this.state.linking || this.state.deleting;
    }

    // Send a change to the linked Google account and show how it went. done is what to say when it works.
    changeGoogle(request, done) {
        this.setState({ linking: true, googleError: null, googleDone: null });
        request.then((settings) => {
            this.setState({
                linking: false, googleDone: done, googlePassword: '', confirmingUnlink: false,
                saved: { ...this.state.saved, googleLinked: settings.googleLinked }
            });
        }).catch((err) => {
            let data = err.response && err.response.data;
            if (err.response && err.response.status === 401) {
                // the login ran out while the form was open
                this.props.onClose();
                return;
            }
            // the backend calls the current password currentPassword; here that is the googlePassword field
            let field = data && data.field === 'currentPassword' ? 'googlePassword' : null;
            this.setState({ linking: false, googleError: { field, message: (data && data.message) || "Couldn't change your Google sign-in. Please try again." } });
        });
    }

    // Google's button has been used: link the account it signed in to this one
    linkGoogle = (credential) => {
        let { saved, googlePassword } = this.state;
        if (saved.hasPassword && googlePassword === '') {
            this.setState({ googleError: { field: 'googlePassword', message: 'Enter your current password, then choose your Google account again' }, googleDone: null });
            return;
        }
        this.changeGoogle(api.profile.linkGoogle(credential, saved.hasPassword ? sha256(googlePassword) : undefined), 'linked');
    }

    unlinkGoogle = (e) => {
        e.preventDefault();
        if (this.state.googlePassword === '') {
            this.setState({ googleError: { field: 'googlePassword', message: 'Enter your current password to unlink your Google account' } });
            return;
        }
        this.changeGoogle(api.profile.unlinkGoogle(sha256(this.state.googlePassword)), 'unlinked');
    }

    // The Google part: linking a Google account to sign in with, or unlinking the one that is. Left out when the
    // site has no Google sign-in and there is nothing linked to show.
    renderGoogle() {
        let { saved, confirmingUnlink, linking, googleError, googleDone } = this.state;
        if (!saved.googleLinked && !GOOGLE_CLIENT_ID) {
            return null;
        }
        let password = (hint) => this.field('googlePassword', 'Current password', { type: 'password' }, hint, googleError);
        let done = googleDone === 'linked' ? 'Your Google account is linked. You can now sign in with Google.'
            : googleDone === 'unlinked' ? 'Your Google account has been unlinked.' : null;
        let wholeError = googleError && googleError.field === null ? <p className="pf-error" role="alert">{googleError.message}</p> : null;
        if (!saved.googleLinked) {
            return (
                <div className="pf-google">
                    <h3 className="pf-settings-sub">Sign in with Google</h3>
                    <p className="pf-field-hint pf-settings-note">Link a Google account to sign in with it as well as your password. Your cards, scores and everything else stay as they are.</p>
                    {saved.hasPassword ? password('Enter it first, then choose your Google account.') : null}
                    {linking ? <p className="pf-field-hint">Linking...</p>
                        : <GoogleSignInButton text="continue_with" onCredential={this.linkGoogle}
                            onError={() => this.setState({ googleError: { field: null, message: "Couldn't load Google sign-in. Please try again." } })} />}
                    {wholeError}
                    {done ? <p className="pf-done" role="status">{done}</p> : null}
                </div>
            );
        }
        return (
            <div className="pf-google">
                <h3 className="pf-settings-sub">Sign in with Google</h3>
                {done ? <p className="pf-done pf-settings-note" role="status">{done}</p>
                    : <p className="pf-field-hint pf-settings-note">A Google account is linked. You can sign in with Google.</p>}
                {!saved.hasPassword ?
                    <p className="pf-field-hint pf-settings-note">It is your only way to sign in. Set a password above if you want to unlink it.</p>
                    : confirmingUnlink ?
                        <form onSubmit={this.unlinkGoogle} noValidate>
                            {password('After unlinking you sign in with your username and password only.')}
                            {wholeError}
                            <div className="pf-settings-actions">
                                <button type="button" className="pf-btn pf-btn-ghost" disabled={linking} onClick={() => this.setState({ confirmingUnlink: false, googlePassword: '', googleError: null })}>Keep it linked</button>
                                <button type="submit" className="pf-btn pf-btn-primary" disabled={linking}>{linking ? 'Unlinking...' : 'Unlink Google account'}</button>
                            </div>
                        </form>
                        :
                        <button type="button" className="pf-btn pf-btn-ghost" onClick={() => this.setState({ confirmingUnlink: true, googleDone: null, googleError: null })}>Unlink Google account</button>
                }
            </div>
        );
    }

    // Changing the email of an account that has a password takes the current one
    needsCurrentPassword() {
        let { saved, email } = this.state;
        return Boolean(saved && saved.hasPassword && email.trim() !== saved.email);
    }

    // Change the password: the current one (if the account has one), and the new one typed the same way twice
    changePassword = (e) => {
        e.preventDefault();
        let { saved, oldPassword, newPassword, confirmPassword } = this.state;
        let refuse = (field, message) => this.setState({ passwordError: { field, message }, passwordChanged: false });
        if (saved.hasPassword && oldPassword === '') {
            return refuse('oldPassword', 'Enter your current password');
        }
        if (newPassword.length < PASSWORD_MIN_LENGTH) {
            return refuse('newPassword', `Your new password must be at least ${PASSWORD_MIN_LENGTH} characters`);
        }
        if (confirmPassword === '') {
            return refuse('confirmPassword', 'Type your new password again to confirm it');
        }
        if (newPassword !== confirmPassword) {
            return refuse('confirmPassword', "The new passwords don't match");
        }
        if (saved.hasPassword && newPassword === oldPassword) {
            return refuse('newPassword', 'Your new password is the same as your current one');
        }
        // passwords go hashed, as they do when logging in
        let changes = { newPassword: sha256(newPassword) };
        if (saved.hasPassword) {
            changes.currentPassword = sha256(oldPassword);
        }
        this.setState({ changingPassword: true, passwordError: null, passwordChanged: false });
        api.profile.saveSettings(changes).then((settings) => {
            // the account now has a password, whether or not it did before
            this.setState({
                changingPassword: false, passwordChanged: true, oldPassword: '', newPassword: '', confirmPassword: '',
                saved: { ...this.state.saved, hasPassword: settings.hasPassword }
            });
        }).catch((err) => {
            let data = err.response && err.response.data;
            if (err.response && err.response.status === 401) {
                // the login ran out while the form was open
                this.props.onClose();
                return;
            }
            // the backend calls the current password currentPassword; here that is the oldPassword field
            let field = data && data.field === 'currentPassword' ? 'oldPassword' : (data && data.field) || null;
            this.setState({ changingPassword: false, passwordError: { field, message: (data && data.message) || "Couldn't change your password. Please try again." } });
        });
    }

    save = (e) => {
        e.preventDefault();
        let { saved, username, email, bio, hidePresence, friendsVisibility, currentPassword } = this.state;
        if (this.needsCurrentPassword() && currentPassword === '') {
            this.setState({ error: { field: 'currentPassword', message: 'Enter your current password to change your email' } });
            return;
        }

        // only what has changed is sent; passwords go hashed, as they do when logging in
        let changes = {};
        if (username.trim() !== saved.username) {
            changes.username = username.trim();
        }
        if (email.trim() !== saved.email) {
            changes.email = email.trim();
        }
        if (bio !== saved.bio) {
            changes.bio = bio;
        }
        if (hidePresence !== Boolean(saved.hidePresence)) {
            changes.hidePresence = hidePresence;
        }
        if (friendsVisibility !== (saved.friendsVisibility || 'public')) {
            changes.friendsVisibility = friendsVisibility;
        }
        if (this.needsCurrentPassword()) {
            changes.currentPassword = sha256(currentPassword);
        }
        if (Object.keys(changes).length === 0) {
            this.props.onClose();
            return;
        }

        this.setState({ saving: true, error: null });
        api.profile.saveSettings(changes).then((settings) => {
            this.props.onSaved(settings);
        }).catch((err) => {
            let data = err.response && err.response.data;
            if (err.response && err.response.status === 401) {
                // the login ran out while the form was open
                this.props.onClose();
                return;
            }
            this.setState({ saving: false, error: { field: (data && data.field) || null, message: (data && data.message) || "Couldn't save your settings. Please try again." } });
        });
    }

    deleteAccount = (e) => {
        e.preventDefault();
        let { saved, deleteConfirm } = this.state;
        if (deleteConfirm === '') {
            this.setState({ deleteError: saved.hasPassword ? 'Enter your password to delete your account' : 'Type your username to delete your account' });
            return;
        }
        this.setState({ deleting: true, deleteError: null });
        // the password goes hashed, as it does when logging in
        api.profile.deleteAccount(saved.hasPassword ? { currentPassword: sha256(deleteConfirm) } : { username: deleteConfirm }).then(() => {
            this.props.onDeleted();
        }).catch((err) => {
            let data = err.response && err.response.data;
            if (err.response && err.response.status === 401) {
                // the login ran out while the form was open
                this.props.onClose();
                return;
            }
            this.setState({ deleting: false, deleteError: (data && data.message) || "Couldn't delete your account. Please try again." });
        });
    }

    // The last part of the form: a button that opens a confirmation, which asks for the password before deleting
    renderDelete() {
        let { saved, confirmingDelete, deleteConfirm, deleting, deleteError } = this.state;
        return (
            <div className="pf-danger">
                <h3 className="pf-settings-sub">Delete account</h3>
                <p className="pf-field-hint pf-settings-note">
                    Deletes your profile for good, with your cards, diamonds, upgrades and scores. This can't be undone.
                </p>
                {confirmingDelete ?
                    <form onSubmit={this.deleteAccount} noValidate autoComplete="off">
                        <div className="pf-field">
                            <label htmlFor="pf-settings-delete">{saved.hasPassword ? 'Your password' : `Type your username (${saved.username}) to confirm`}</label>
                            <input id="pf-settings-delete" type={saved.hasPassword ? 'password' : 'text'} {...NO_FILL}
                                value={deleteConfirm} autoFocus aria-invalid={Boolean(deleteError)} onChange={(e) => this.setState({ deleteConfirm: e.target.value })} />
                            {deleteError ? <p className="pf-error" role="alert">{deleteError}</p> : null}
                        </div>
                        <div className="pf-settings-actions">
                            <button type="button" className="pf-btn pf-btn-ghost" disabled={deleting} onClick={() => this.setState({ confirmingDelete: false, deleteConfirm: '', deleteError: null })}>Keep my account</button>
                            <button type="submit" className="pf-btn pf-btn-danger" disabled={deleting}>{deleting ? 'Deleting...' : 'Delete my account for good'}</button>
                        </div>
                    </form>
                    :
                    <button type="button" className="pf-btn pf-btn-danger-ghost" onClick={() => this.setState({ confirmingDelete: true })}>Delete account</button>
                }
            </div>
        );
    }

    // The password part: the current password, then the new one twice, with a button of its own
    renderPassword() {
        let { saved, changingPassword, passwordError, passwordChanged } = this.state;
        let password = (name, label) => this.field(name, label, { type: 'password' }, null, passwordError);
        return (
            <form className="pf-password" onSubmit={this.changePassword} noValidate autoComplete="off">
                <h3 className="pf-settings-sub">{saved.hasPassword ? 'Change password' : 'Set a password'}</h3>
                {saved.hasPassword ?
                    password('oldPassword', 'Current password')
                    :
                    <p className="pf-field-hint pf-settings-note">You sign in with Google. Setting a password lets you log in with your username as well.</p>
                }
                {password('newPassword', 'New password')}
                {password('confirmPassword', 'Retype new password')}
                {passwordError && passwordError.field === null ? <p className="pf-error" role="alert">{passwordError.message}</p> : null}
                {passwordChanged ? <p className="pf-done" role="status">Your password has been changed. Your other devices have been logged out.</p> : null}
                <div className="pf-settings-actions">
                    <button type="submit" className="pf-btn pf-btn-primary" disabled={changingPassword}>
                        {changingPassword ? 'Changing...' : saved.hasPassword ? 'Change password' : 'Set password'}
                    </button>
                </div>
            </form>
        );
    }

    // A labelled input, with its form's error under it when the error is about this field. `from` is that form's
    // error ({ field, message }); the details form's unless another is given. `mark` is shown at the right of the
    // input, and `note` under it in place of the hint.
    field(name, label, props, hint, from = this.state.error, mark = null, note = null) {
        let error = from && from.field === name ? from.message : null;
        return (
            <div className="pf-field">
                <label htmlFor={`pf-settings-${name}`}>{label}</label>
                <div className={mark ? 'pf-input-wrap pf-input-marked' : 'pf-input-wrap'}>
                    <input id={`pf-settings-${name}`} name={name} value={this.state[name]} aria-invalid={Boolean(error)}
                        aria-describedby={error ? `pf-settings-${name}-error` : undefined}
                        onChange={(e) => this.setState({ [name]: e.target.value })} {...NO_FILL} {...props} />
                    {mark}
                </div>
                {error ? <p className="pf-error" id={`pf-settings-${name}-error`} role="alert">{error}</p>
                    : note ? note : hint ? <p className="pf-field-hint">{hint}</p> : null}
            </div>
        );
    }

    // The mark beside the username, and what it says under the input, from the check of what's typed
    usernameStatus() {
        let check = this.state.usernameCheck;
        if (!check || check.status === 'checking') {
            return { mark: null, note: null };
        }
        let available = check.status === 'available';
        return {
            mark: (
                <span className={available ? 'pf-input-mark pf-input-mark-ok' : 'pf-input-mark pf-input-mark-bad'}
                    title={available ? 'Available' : check.message} role="img" aria-label={available ? 'Username available' : 'Username unavailable'}>
                    <AvailableIcon available={available} />
                </span>
            ),
            note: available ? null : <p className="pf-error" role="status">{check.message}</p>
        };
    }

    renderForm() {
        let { saved, bio, saving, error } = this.state;
        let bioError = error && error.field === 'bio' ? error.message : null;
        let { mark, note } = this.usernameStatus();
        return (
            <form onSubmit={this.save} noValidate autoComplete="off">
                {this.field('username', 'Username', { type: 'text', maxLength: saved.usernameMaxLength, autoFocus: true, onChange: this.onUsernameChange },
                    `${saved.usernameMinLength} to ${saved.usernameMaxLength} letters (a-z) and numbers (0-9). Your profile's address changes with it.`,
                    error, mark, note)}
                {this.field('email', 'Email', { type: 'email', maxLength: 255 })}
                <div className="pf-field">
                    <label htmlFor="pf-settings-bio">Biography</label>
                    <textarea id="pf-settings-bio" className="pf-bio-input" {...NO_FILL} rows="4" maxLength={saved.bioMaxLength} value={bio}
                        placeholder="Tell other players a bit about yourself." onChange={(e) => this.setState({ bio: e.target.value })} />
                    {bioError ? <p className="pf-error" role="alert">{bioError}</p> : <p className="pf-field-hint">{bio.length} / {saved.bioMaxLength}</p>}
                </div>

                <h3 className="pf-settings-sub">Privacy</h3>
                <label className="pf-check">
                    <input type="checkbox" checked={this.state.hidePresence} onChange={(e) => this.setState({ hidePresence: e.target.checked })} />
                    <span>
                        Hide my online status
                        <span className="pf-field-hint">Other players won't see whether you are online or when you were last seen.</span>
                    </span>
                </label>
                <div className="pf-field pf-visibility">
                    <label htmlFor="pf-settings-friendsVisibility">Who can see my friends list</label>
                    <select id="pf-settings-friendsVisibility" value={this.state.friendsVisibility} onChange={(e) => this.setState({ friendsVisibility: e.target.value })}>
                        {Object.entries(FRIENDS_VISIBILITY_NAMES).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                    </select>
                </div>

                {this.needsCurrentPassword() ?
                    this.field('currentPassword', 'Current password', { type: 'password' }, 'Needed to change your email.')
                    : null}

                {error && error.field === null ? <p className="pf-error" role="alert">{error.message}</p> : null}
                <div className="pf-settings-actions">
                    <button type="button" className="pf-btn pf-btn-ghost" disabled={saving} onClick={this.props.onClose}>Cancel</button>
                    <button type="submit" className="pf-btn pf-btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save changes'}</button>
                </div>
            </form>
        );
    }

    render() {
        let { loading, saved, error } = this.state;
        return (
            <div className="pf-modal" role="dialog" aria-modal="true" aria-labelledby="pf-settings-title" onMouseDown={(e) => e.target === e.currentTarget && !this.busy() && this.props.onClose()}>
                <div className="pf-modal-body">
                    <h2 id="pf-settings-title">Account settings</h2>
                    {loading ? <p className="pf-empty">Loading your settings...</p>
                        : saved ? <>{this.renderForm()}{this.renderPassword()}{this.renderGoogle()}{this.renderDelete()}</>
                        :
                        <>
                            <p className="pf-error" role="alert">{error ? error.message : null}</p>
                            <div className="pf-settings-actions">
                                <button type="button" className="pf-btn pf-btn-ghost" onClick={this.props.onClose}>Close</button>
                            </div>
                        </>
                    }
                </div>
            </div>
        );
    }

}
